import React, { useState, useEffect, useRef } from "react";
import { Add, Remove } from "@mui/icons-material";
import patientService from "../../Services/patientService";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import medicineService from "../../Services/medicineService";
import doctorService from "../../Services/doctorService";
import MedicineInvoiceModal from "../../Components/MedicineInvoiceModal";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
    />
  </div>
);

const SelectField = ({ label, value, onChange, children, required }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <select
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100 appearance-none"
    >
      {children}
    </select>
  </div>
);

const SaleMedicine = () => {
  const [salesRows, setSalesRows] = useState([
    { stock_id: "", quantity: "", unit_price: "", medicineSearch: "", selectedMedicineName: "", isOpen: false },
  ]);
  const [medicineData, setMedicineData] = useState([]);
  const [patientss, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [searchData, setSearchData] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [saleDate] = useState(new Date().toISOString().split("T")[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const navigate = useNavigate();
  const isAddingRow = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [medicinesRes, doctorsRes] = await Promise.all([
          medicineService.fetchAll(),
          doctorService.fetchAllDoctors(),
        ]);
        setMedicineData(medicinesRes.medicines || []);
        setDoctors(doctorsRes.doctors || []);
      } catch {
        toast.error("Error fetching data.");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const getPatients = async () => {
      try {
        const response = await patientService.fetchAllPatients();
        setPatients(response.patients || []);
      } catch {
        toast.error("Error fetching patients.");
      }
    };
    getPatients();
  }, []);

  const handleRowChange = (index, field, value) => {
    const updatedRows = [...salesRows];
    const num = (v) => (v === "" || v == null ? "" : Number(v));

    if (field === "quantity") {
      const q = num(value);
      if (q <= 0) { toast.error("Quantity must be greater than 0."); return; }
      const med = medicineData.find(
        (m) => Number(m.id) === Number(updatedRows[index].stock_id)
      );
      if (med && q > Number(med.quantity_in_stock ?? 0)) {
        toast.error("Quantity exceeds available stock.");
        updatedRows[index].quantity = Number(med.quantity_in_stock ?? 0);
      } else {
        updatedRows[index].quantity = q;
      }
    } else if (field === "unit_price") {
      updatedRows[index].unit_price = num(value);
    } else {
      updatedRows[index][field] = value;
    }

    setSalesRows(updatedRows);
  };

  const handleMedicineSelect = (index, med) => {
    const updatedRows = [...salesRows];
    updatedRows[index].stock_id = Number(med.id);
    updatedRows[index].unit_price = med.discounted_price ?? med.selling_price ?? "";
    updatedRows[index].selectedMedicineName = med.medicine_name;
    updatedRows[index].medicineSearch = "";
    updatedRows[index].isOpen = false;
    if (!updatedRows[index].quantity) updatedRows[index].quantity = 1;
    setSalesRows(updatedRows);
  };

  const handleAddRow = () =>
    setSalesRows([...salesRows, { stock_id: "", quantity: "", unit_price: "", medicineSearch: "", selectedMedicineName: "", isOpen: false }]);

  const handleRemoveRow = (index) =>
    setSalesRows(salesRows.filter((_, i) => i !== index));

  const handleClearFirstRow = () => {
    const updatedRows = [...salesRows];
    updatedRows[0] = {
      stock_id: "",
      quantity: "",
      unit_price: "",
      medicineSearch: "",
      selectedMedicineName: "",
      isOpen: false,
    };
    setSalesRows(updatedRows);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSelectedPatientId(patient.patient_id);
    setSearchData("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatientId) {
      toast.error("Please select a patient before submitting.");
      return;
    }
    setIsModalOpen(true);
  };

  const grandTotal = salesRows.reduce(
    (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unit_price) || 0),
    0
  );

  const firstRowHasData = (row) =>
    row.stock_id || row.selectedMedicineName || row.quantity || row.unit_price;

  return (
    <>
      <div className="bg-gray-50 flex items-start justify-center py-4 sm:py-6 md:py-10 px-3 sm:px-4">
        <div className="w-full max-w-8xl">
          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-[#215187] px-4 sm:px-6 md:px-8 py-4 sm:py-5">
              <h1 className="text-white text-[15px] sm:text-[17px] md:text-[18px] font-[500] tracking-tight">
                Record Medicine Sales
              </h1>
              <p className="text-gray-400 text-[11px] sm:text-[12px] mt-0.5">
                Select a patient, doctor, and add medicines to complete the sale
              </p>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6">

              <SectionLabel>Sale Information</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">

                <div className="flex flex-col gap-1 relative">
                  <label className="text-[12px] font-[500] text-gray-500">Patient</label>
                  <input
                    type="text"
                    placeholder="Type to search patients..."
                    value={searchData || selectedPatient?.full_name || ""}
                    onChange={(e) => {
                      setSearchData(e.target.value);
                      if (selectedPatient) setSelectedPatient(null);
                    }}
                    className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
                  />
                  {searchData && (
                    <div className="absolute top-[62px] left-0 right-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <div className="grid grid-cols-3 px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="text-[11px] font-[600] text-gray-400 uppercase">P No.</span>
                        <span className="text-[11px] font-[600] text-gray-400 uppercase">Name</span>
                        <span className="text-[11px] font-[600] text-gray-400 uppercase">Phone</span>
                      </div>
                      {patientss
                        .filter(
                          (p) =>
                            p.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
                            p.patient_id?.toString().includes(searchData)
                        )
                        .slice(0, 10)
                        .map((patient) => (
                          <button
                            key={patient.patient_id}
                            type="button"
                            onClick={() => handlePatientSelect(patient)}
                            className="w-full grid grid-cols-3 px-3 py-2 text-[13px] text-left hover:bg-orange-50 border-b border-gray-50 transition-colors"
                          >
                            <span className="text-gray-500">{patient.patient_id}</span>
                            <span className="text-gray-800 font-[500] truncate">{patient.full_name}</span>
                            <span className="text-gray-500">{patient.contact_number}</span>
                          </button>
                        ))}
                      {patientss.filter(
                        (p) =>
                          p.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
                          p.patient_id?.toString().includes(searchData)
                      ).length === 0 && (
                        <p className="px-3 py-3 text-[13px] text-gray-400 text-center">
                          No patients found
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <SelectField
                  label="Doctor"
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  required
                >
                  <option value="" disabled>Select doctor</option>
                  {doctors.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.name}
                    </option>
                  ))}
                </SelectField>

                <InputField
                  label="Sale Date"
                  type="date"
                  value={saleDate}
                  readOnly
                />
              </div>

              <SectionLabel>Medicine Items</SectionLabel>
              <div className="hidden sm:grid grid-cols-12 gap-3 mb-2 px-1">
                <span className="col-span-5 text-[11px] uppercase tracking-wide font-[600] text-gray-400">Medicine</span>
                <span className="col-span-3 text-[11px] uppercase tracking-wide font-[600] text-gray-400">Quantity</span>
                <span className="col-span-3 text-[11px] uppercase tracking-wide font-[600] text-gray-400">Unit Price (Rs.)</span>
                <span className="col-span-1" />
              </div>

              <div className="flex flex-col gap-3">
                {salesRows.map((row, index) => {
                  const rowTotal =
                    (Number(row.quantity) || 0) * (Number(row.unit_price) || 0);
                  return (
                    <div
                      key={index}
                      className="bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-3"
                    >
                      <div className="flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:gap-3 sm:items-center">
                        <div className="sm:col-span-5 relative">
                          <label className="sm:hidden text-[11px] uppercase tracking-wide font-[600] text-gray-400 mb-1 block">Medicine</label>
                          <input
                            type="text"
                            placeholder="Type to search medicine..."
                            value={row.medicineSearch || row.selectedMedicineName || ""}
                            onFocus={() => {
                              const updatedRows = [...salesRows];
                              updatedRows[index].isOpen = true;
                              setSalesRows(updatedRows);
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                if (!isAddingRow.current) {
                                  const updatedRows = [...salesRows];
                                  updatedRows[index].isOpen = false;
                                  setSalesRows(updatedRows);
                                }
                                isAddingRow.current = false;
                              }, 200);
                            }}
                            onChange={(e) => {
                              const updatedRows = [...salesRows];
                              updatedRows[index].medicineSearch = e.target.value;
                              updatedRows[index].isOpen = true;
                              if (row.selectedMedicineName) updatedRows[index].selectedMedicineName = "";
                              setSalesRows(updatedRows);
                            }}
                            className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
                          />
                          {row.isOpen && (
                            <div className="absolute top-[38px] left-0 right-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {medicineData
                                .filter((m) =>
                                  !row.medicineSearch ||
                                  m.medicine_name?.toLowerCase().includes(row.medicineSearch.toLowerCase())
                                )
                                .map((med) => (
                                  <button
                                    key={med.id}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleMedicineSelect(index, med)}
                                    className="w-full px-3 py-2 text-[13px] text-left text-gray-800 font-[500] hover:bg-orange-50 border-b border-gray-50 transition-colors"
                                  >
                                    {med.medicine_name}
                                  </button>
                                ))}
                              {medicineData.filter((m) =>
                                !row.medicineSearch ||
                                m.medicine_name?.toLowerCase().includes(row.medicineSearch.toLowerCase())
                              ).length === 0 && (
                                <p className="px-3 py-3 text-[13px] text-gray-400 text-center">
                                  No medicines found
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 sm:contents">
                          <div className="flex-1 sm:col-span-3">
                            <label className="sm:hidden text-[11px] uppercase tracking-wide font-[600] text-gray-400 mb-1 block">Quantity</label>
                            <input
                              type="number"
                              placeholder="Qty"
                              value={row.quantity}
                              onChange={(e) => handleRowChange(index, "quantity", e.target.value)}
                              required
                              className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
                            />
                          </div>

                          <div className="flex-1 sm:col-span-3">
                            <label className="sm:hidden text-[11px] uppercase tracking-wide font-[600] text-gray-400 mb-1 block">Unit Price</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-400">
                                Rs.
                              </span>
                              <input
                                type="number"
                                placeholder="0"
                                value={row.unit_price}
                                readOnly
                                className="w-full pl-8 pr-3 py-2 text-[13px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg outline-none cursor-not-allowed"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="sm:col-span-1 flex sm:flex-col items-center justify-between sm:justify-center gap-1">
                          {rowTotal > 0 && (
                            <span className="text-[10px] text-gray-400 font-[500] whitespace-nowrap">
                              Rs.{rowTotal.toLocaleString()}
                            </span>
                          )}
                          {index === 0 ? (
                            firstRowHasData(row) && (
                              <button
                                type="button"
                                onClick={handleClearFirstRow}
                                title="Clear"
                                className="text-gray-400 hover:text-red-500 transition-colors text-[11px] font-[600] border border-gray-200 hover:border-red-300 rounded px-1.5 py-0.5"
                              >
                                ✕
                              </button>
                            )
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(index)}
                              className="text-red-400 hover:text-red-600 transition-colors"
                            >
                              <Remove style={{ fontSize: 18 }} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onMouseDown={() => { isAddingRow.current = true; }}
                onClick={handleAddRow}
                className="mt-3 flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-800 font-[500] transition-colors"
              >
                <Add style={{ fontSize: 16 }} />
                Add another medicine
              </button>

              {grandTotal > 0 && (
                <div className="mt-5 flex justify-end">
                  <div className="bg-[#215187] text-white rounded-xl px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
                    <span className="text-[11px] sm:text-[12px] text-white uppercase tracking-wide">Grand Total</span>
                    <span className="text-[16px] sm:text-[18px] font-[600]">Rs. {grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 mt-6 sm:mt-8 pt-4 sm:pt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500]"
                >
                  ← Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="bg-[#2F6DB4] hover:bg-[#255A8C] transition-colors text-white text-[13px] font-[500] px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg"
                >
                  Save Sale →
                </button>
              </div>

            </div>
          </div>

          <p className="text-center text-[12px] text-gray-400 mt-4">
            An invoice will be generated after saving the sale.
          </p>
        </div>
      </div>

      <MedicineInvoiceModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        patientName={selectedPatient?.full_name || ""}
        doctorName={doctors.find((doc) => doc.id === selectedDoctor)?.name || ""}
        servicesName={salesRows.map((row) => {
          const med = medicineData.find((m) => Number(m.id) === Number(row.stock_id));
          return med ? med.medicine_name : "N/A";
        })}
        patientId={selectedPatientId}
        doctorId={selectedDoctor}
        saleDate={saleDate}
        salesRows={salesRows}
        invoiceId={invoiceId}
      />
    </>
  );
};

export default SaleMedicine;